import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { MatTableDataSource } from '@angular/material/table'
import {
  AppointmentDataService,
  AppointmentModel,
  ConfirmService,
  CrudService,
  INSURANCE_PROVIDER_LOOKUP_TYPE,
  Patient,
  PATIENT_DATA_PROGRESS_KEY,
  PatientDataService,
  PatientInsurance,
  PatientInsuranceStatus,
} from '@mmx/shared'
import { orderBy, without } from 'lodash'
import { concatMap, filter, Subject, takeUntil, tap } from 'rxjs'

import { InsuranceDialogComponent } from '../insurance-dialog'
import { PullEligibilityDialogComponent, PullEligibilityDialogInput } from '../pull-eligibility-dialog'

@Component({
  selector: 'mmx-insurances-card',
  templateUrl: './insurances-card.component.html',
  standalone: false,
})
export class InsurancesCardComponent implements OnInit, OnDestroy {

  @Input() embedded = false

  public patient: Patient
  public appointment?: AppointmentModel
  public insuranceTableColumns = [
    'carrier',
    'status',
    'member',
    'type',
    'actions',
  ]
  public dataSource = new MatTableDataSource<PatientInsurance>([])

  public INSURANCE_PROVIDER_LOOKUP_TYPE = INSURANCE_PROVIDER_LOOKUP_TYPE
  public PatientInsuranceStatus = PatientInsuranceStatus

  public progress$ = this.patientDataService.progressService.inProgress(
    PATIENT_DATA_PROGRESS_KEY.PATIENT,
    PATIENT_DATA_PROGRESS_KEY.INSURANCES,
  )

  private onDestroy$ = new Subject<void>()

  constructor(
    private dialog: MatDialog,
    private patientDataService: PatientDataService,
    private confirm: ConfirmService,
    private crudService: CrudService,
    private appointmentDataService: AppointmentDataService,
  ) {}

  ngOnInit(): void {
    this.appointmentDataService.appointment$.pipe(takeUntil(this.onDestroy$)).subscribe(appointment => this.appointment = appointment)
    this.patientDataService.patient$.pipe(takeUntil(this.onDestroy$)).subscribe(patient => this.patient = patient)
    this.patientDataService.insurances$
      .pipe(
        takeUntil(this.onDestroy$),
        tap(
          (insurances) =>
            (this.dataSource.data = orderBy(insurances, 'typeIndex')),
        ),
      )
      .subscribe()
  }

  ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  onAddInsuranceClick() {
    const dialog = this.dialog.open(InsuranceDialogComponent, {
      panelClass: 'dialog-form',
      data: {
        patient: this.patient,
        appointment: this.appointment,
        insurances: this.dataSource.data,
      },
    })

    dialog.afterClosed().subscribe((insurance) => {
      if (insurance) {
        this.patientDataService.insurancesChanged$.next()
      }
    })
  }

  onEditInsuranceClick(insurance: PatientInsurance) {
    const dialog = this.dialog.open(InsuranceDialogComponent, {
      panelClass: 'dialog-form',
      data: {
        patient: this.patient,
        appointment: this.appointment,
        insurance,
        insurances: without(this.dataSource.data, insurance),
      },
    })

    dialog.afterClosed().subscribe((insurance) => {
      if (insurance) {
        this.patientDataService.insurancesChanged$.next()
      }
    })
  }

  onPullEligibilityClick(insurance: PatientInsurance) {
    const data: PullEligibilityDialogInput = {
      patient: this.patient,
      appointment: this.appointment,
      insurances: this.dataSource.data,
      insurance,
      insuranceType: insurance.type,
    }
    const dialog = this.dialog.open(PullEligibilityDialogComponent, {
      panelClass: 'dialog-form',
      width: '500px',
      data,
    })
    dialog
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        filter(Boolean),
        tap(() => {
          this.patientDataService.insurancesChanged$.next()
          this.appointmentDataService.appointmentUpdated$.next()
        }),
      )
      .subscribe()
  }

  deleteInsurance(insurance: PatientInsurance): void {
    this.confirm
      .confirm('patient-details.insurance-card.confirm-delete')
      .pipe(
        takeUntil(this.onDestroy$),
        filter((yes) => yes),
        concatMap(() =>
          this.crudService.delete(insurance, {
            appointmentId: this.appointment?.id,
          }),
        ),
        tap(() => {
          this.patientDataService.insurancesChanged$.next()
        }),
      )
      .subscribe()
  }
}
